/**
 * Small pure readings of a release document, for the pages that look at releases the
 * operator is *not* currently deploying — the list and the per-release page.
 *
 * Separate from `componentRows.ts` because those need the live component documents and
 * this release's instructions, which only exist for the release in front of you. A
 * closed release from three months ago has nothing but its own document, and these are
 * the questions that document can still answer.
 */
import type { Acknowledgement, ComponentPlanEntry, ReleaseDoc } from '@finvari/deployer';

/** As much of a release as the "what ships" readings below need. */
type Shipped = Pick<ReleaseDoc, 'releaseTag' | 'releaseCommit' | 'deployCommit' | 'postTagCommits'>;

/**
 * The ref this release actually ships: the release branch tip the host deployed.
 *
 * Named separately from the tag because they are not always the same commit — a fix
 * pushed to `release` during an open release ships with it — and every "what is in
 * this release" question (the compare link, the commit shown on the page) has to be
 * about what production will run, not about what the announcement described. Falls
 * back to the tag for a release whose record was frozen at close without one.
 */
export function shippedRef(release: Shipped): string {
    return release.deployCommit ?? release.releaseTag;
}

/**
 * How the release differs from its tag, as a phrase to put beside the commit, or
 * undefined when the tip is the tag. Spelled out on the page because a commit the
 * announcement does not name is exactly the thing nobody would think to check.
 */
export function pastTagText(release: Shipped): string | undefined {
    const commits = release.postTagCommits ?? 0;
    if (commits === 0) return undefined;
    return `${commits} commit${commits === 1 ? '' : 's'} past ${release.releaseTag} (${release.releaseCommit.slice(0, SHORT_SHA)})`;
}

/** How much of a sha the pages show. */
export const SHORT_SHA = 12;

/**
 * The components a release contains, in registry deploy order — `compile-only`
 * ones included. This is the historical record, and a component that was rebuilt
 * and deliberately not deployed is part of what happened; the table shows its
 * kind, so it reads as the decision it was rather than as missing work.
 */
export function changedComponents(plan: Record<string, ComponentPlanEntry> | undefined): string[] {
    return Object.entries(plan ?? {})
        .filter(([, entry]) => entry.changeKind !== 'none')
        .sort(([aName, a], [bName, b]) => a.deployGroup - b.deployGroup || aName.localeCompare(bName))
        .map(([name]) => name);
}

export interface SkippedComponent {
    name: string;
    acknowledgement: Acknowledgement;
}

/**
 * What a release shipped without. The reason on each is the whole point of the record:
 * a component this release changed and production never got is a difference between
 * the tag and the running system, and the acknowledgement is the only explanation of
 * it anyone will find later.
 */
export function skippedComponents(
    plan: Record<string, ComponentPlanEntry> | undefined,
    acknowledgements: Record<string, Acknowledgement> | undefined,
): SkippedComponent[] {
    return changedComponents(plan)
        .map((name) => ({ name, acknowledgement: acknowledgements?.[name] }))
        .filter((s): s is SkippedComponent => !!s.acknowledgement);
}

/**
 * How the component table's deploy column reads for a component nothing deployed.
 *
 * `compile-only` gets its own phrase because "no record here" would be read as a
 * missing record. There is nothing missing: the release decided this component
 * needed no deploy, and that decision is the record.
 */
export function deployOutcomeText(
    entry: ComponentPlanEntry | undefined,
    acknowledgement: Acknowledgement | undefined,
): string {
    if (acknowledgement) return 'skipped';
    if (entry?.changeKind === 'compile-only') return 'no deploy needed';
    return 'no record here';
}
